import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Modal,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Typography,
  Grid,
} from '@material-ui/core';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PhotoCamera from '@material-ui/icons/PhotoCamera';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import parse from 'autosuggest-highlight/parse';
import debounce from 'lodash/debounce';
import '../assets/styles/CustomPropertyModal.css';

// New imports for carousel and lazy loading
import Carousel from 'react-material-ui-carousel';
import LazyLoad from 'react-lazyload';

const GOOGLE_MAPS_API_KEY = "AIzaSyDXcuNu9hM04ec2v4lqzrfcrmIKjILZMRU";

const dropdownData = {
  bedrooms: ["0", "1", "2", "3", "4", "5"],
  bathrooms: ["0", "1", "2", "3", "4"],
  parkingSpaces: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
  basementOptions: ["Finished", "Unfinished", "Walk Out", "Apartment", "None"],
  ageOptions: ["New", "0-5", "6-15", "16-30", "31-50", "100+"],
  acOptions: ["Central Air", "Wall Unit", "None"],
  squareFootRanges: ["< 700", "700-1100", "1100-1500", "1500-2000", "2000-2500", "2500-3000", "3000-3500", "3500-5000", "5000+"],
};

function loadScript(src, position, id) {
  if (!position) return;
  if (document.getElementById(id)) return;

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const CustomPropertyModal = ({ open, onClose, onSave, initialData }) => {
  const [status, setStatus] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [parkingSpaces, setParkingSpaces] = useState('');
  const [basement, setBasement] = useState('');
  const [approxAge, setApproxAge] = useState('');
  const [ac, setAc] = useState('');
  const [squareFootage, setSquareFootage] = useState(null);

  // Support multiple file uploads
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  const [addressError, setAddressError] = useState(false);
  const [mlNum, setMlNum] = useState(null);
  const [listingPrice, setListingPrice] = useState(null);
  const [soldPrice, setSoldPrice] = useState(null);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [latLng, setLatLng] = useState({ lat: null, lng: null });
  const loaded = useRef(false);
  const autocompleteService = useRef(null);

  const [imageDataUrls, setImageDataUrls] = useState([]);   // NEW


  // Initialize fields on open or when initialData changes
  useEffect(() => {
    if (initialData) {
      setBedrooms(initialData.Br);
      setBathrooms(initialData.Bath_tot);
      setParkingSpaces(initialData.Tot_park_spcs);
      setBasement(initialData.Bsmt1_out);
      setApproxAge(initialData.Yr_built);
      setAc(initialData.A_c);
      setSquareFootage(initialData.Sqft);
      setValue({ description: initialData.full_address });
      setStatus(initialData.Lsc);
      setLatLng({ lat: initialData.Latitude, lng: initialData.Longitude });
      setMlNum(initialData.Ml_num);
      setListingPrice(initialData.Lp_dol);
      setSoldPrice(initialData.Sp_dol);

      // Handle existing images array or single image
      if (initialData.imageUrls && initialData.imageUrls.length > 0) {
          setImagePreviews(initialData.imageUrls);
          setImageDataUrls(initialData.imageUrls);        // keep them editable
      } else if (
          initialData.images &&
          Array.isArray(initialData.images) &&
          initialData.images.length > 0
      ) {
          setImagePreviews(initialData.images);
      } else if (initialData.image) {
          setImagePreviews([initialData.image]);
      } else if (initialData.Ml_num) {
        // Fallback to MLS images
        const previews = [];
        for (let i = 1; i <= 30; i++) {
          previews.push(`https://realtime-home-images.s3.amazonaws.com/${initialData.Ml_num}/${i}.jpeg`);
        }
        setImagePreviews(previews);
      } else {
        setImagePreviews([]);
      }

    } else {
      setBedrooms('');
      setBathrooms('');
      setParkingSpaces('');
      setBasement('');
      setApproxAge('');
      setAc('');
      setSquareFootage(null);
      setValue(null);
      setStatus('');
      setLatLng({ lat: null, lng: null });
      setMlNum(null);
      setListingPrice(null);
      setSoldPrice(null);
      setImages([]);
      setImagePreviews([]);
      setImageDataUrls([]);
      setCurrentImageIndex(0);
    }
  }, [initialData]);

  useEffect(() => {
    if (open && initialData === null) {
      setBedrooms('');
      setBathrooms('');
      setParkingSpaces('');
      setBasement('');
      setApproxAge('');
      setAc('');
      setSquareFootage(null);
      setValue(null);
      setStatus('');
      setLatLng({ lat: null, lng: null });
      setMlNum(null);
      setListingPrice(null);
      setSoldPrice(null);
      setImages([]);
      setImagePreviews([]);
      setImageDataUrls([]);
      setCurrentImageIndex(0);
      setInputValue('');
      setOptions([]);
      setAddressError(false);
    }
  }, [open, initialData]);


  useEffect(() => {
    if (window.google && !autocompleteService.current) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
  }, [window.google]);

  const fetch = useMemo(
    () => debounce((request, callback) => {
      if (autocompleteService.current) {
        autocompleteService.current.getPlacePredictions(request, callback);
      } else {
        callback([]);
      }
    }, 400), []
  );

  useEffect(() => {
    if (typeof window !== 'undefined' && !loaded.current) {
      if (!document.querySelector('#google-maps')) {
        loadScript(
          `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
          document.querySelector('head'),
          'google-maps'
        );
      }
      loaded.current = true;
    }
  }, []);

  useEffect(() => {
    let active = true;
    if (!autocompleteService.current) return;
    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return;
    }

    const locationRestriction = {
      north: 44.009388,
      south: 43.402529,
      east: -78.767335,
      west: -79.960514
    };

    fetch({
      input: inputValue,
      locationRestriction,
      componentRestrictions: { country: 'ca' },
    }, (results) => {
      if (active) {
        let newOptions = value ? [value] : [];
        if (results) newOptions = [...newOptions, ...results];
        setOptions(newOptions);
      }
    });

    return () => { active = false; };
  }, [value, inputValue, fetch]);

  const getPlaceDetails = (placeId, callback) => {
    if (!window.google) return;
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails({ placeId }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        callback(place);
      }
    });
  };

  // Handle multiple image uploads
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
  
    // 1) local previews for the carousel (unchanged)
    setImages(files);
    setImagePreviews(files.map((f) => URL.createObjectURL(f)));
    setCurrentImageIndex(0);
  
    // 2) convert every File → data:image/...;base64,
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageDataUrls((prev) => [...prev, event.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Combine user uploads and MLS images
  const finalSubjectPropertyImageArray = useMemo(() => {
    const previews = [...imagePreviews];
  
    // local base-64 versions
    imageDataUrls.forEach((u) => {
      if (!previews.includes(u)) previews.push(u);
    });
  
    // MLS fall-back (unchanged)
    if (mlNum) {
      for (let i = 1; i <= 5; i++) {
        const url = `https://realtime-home-images.s3.amazonaws.com/${mlNum}/${i}.jpeg`;
        if (!previews.includes(url)) previews.push(url);
      }
    }
    return previews;
  }, [imagePreviews, imageDataUrls, mlNum]);

  const handleSave = () => {
    if (!value || !value.description) {
      setAddressError(true);
      return;
    }

    const customPropertyData = {
      full_address: value.description,
      Br: bedrooms,
      Bath_tot: bathrooms,
      Tot_park_spcs: parkingSpaces,
      Bsmt1_out: basement,
      Yr_built: approxAge,
      A_c: ac,
      Sqft: squareFootage,
      images,        // array of uploaded File objects
      imageUrls: imageDataUrls.length
        ? imageDataUrls                         // NEW – data-URIs for custom pics
        : finalSubjectPropertyImageArray,
      Latitude: latLng.lat,
      Longitude: latLng.lng,
      Lsc: status,
      Ml_num: mlNum,
      Lp_dol: listingPrice,
      Sp_dol: soldPrice
    };

    onSave(customPropertyData);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} className="custom-modal">
      <div className="customprop-modal-content">
        <Typography variant="h5" className="modal-header">
          Custom Property Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              getOptionLabel={(option) => typeof option === 'string' ? option : option.description}
              filterOptions={(x) => x}
              options={options}
              autoComplete
              includeInputInList
              filterSelectedOptions
              value={value}
              noOptionsText="No locations"
              onChange={(event, newValue) => {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
                setAddressError(false);
                if (newValue && newValue.place_id) {
                  getPlaceDetails(newValue.place_id, (placeDetails) => {
                    const loc = placeDetails?.geometry?.location;
                    if (loc) setLatLng({ lat: loc.lat(), lng: loc.lng() });
                  });
                }
              }}
              onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Address"
                  fullWidth
                  margin="normal"
                  required
                  error={addressError}
                  helperText={addressError ? "Address is required" : ""}
                />
              )}
              renderOption={(option) => {
                const matches = option.structured_formatting.main_text_matched_substrings || [];
                const parts = parse(option.structured_formatting.main_text, matches.map(m => [m.offset, m.offset + m.length]));
                return (
                  <div>
                    <Grid container alignItems="center">
                      <Grid item style={{ display: 'flex', width: 44 }}>
                        <LocationOnIcon style={{ color: 'text.secondary' }} />
                      </Grid>
                      <Grid item style={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                        {parts.map((part, idx) => (
                          <Box key={idx} component="span" sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}>
                            {part.text}
                          </Box>
                        ))}
                        <Typography variant="body2" style={{ color: 'gray' }}>
                          {option.structured_formatting.secondary_text}
                        </Typography>
                      </Grid>
                    </Grid>
                  </div>
                );
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <div className="image-container">
              {finalSubjectPropertyImageArray.length > 0 ? (
                <Carousel
                  autoPlay={false}
                  navButtonsAlwaysVisible
                  indicators={false}
                  className="carousel-container"
                  index={currentImageIndex}
                  onChange={setCurrentImageIndex}
                >
                  {finalSubjectPropertyImageArray.map((url, idx) => (
                    <LazyLoad key={idx} height={300} offset={100} once>
                      <img
                        src={url}
                        alt={value?.description || 'No Image'}
                        className="carousel-image"
                        style={{ width: '100%', height: '300px', objectFit: 'cover' }}
                      />
                    </LazyLoad>
                  ))}
                </Carousel>
              ) : (
                <div className="image-placeholder">
                  <IconButton color="primary" aria-label="upload pictures" component="label">
                    <input hidden accept="image/*" type="file" multiple onChange={handleImageChange} />
                    <PhotoCamera />
                  </IconButton>
                  <p>Click to upload images</p>
                </div>
              )}
            </div>
          </Grid>
                    {/* Bedrooms */}
                    <Grid item xs={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Bedrooms</InputLabel>
              <Select
                value={bedrooms || ''}
                onChange={(e) => setBedrooms(e.target.value)}
              >
                {dropdownData.bedrooms.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Bathrooms */}
          <Grid item xs={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Bathrooms</InputLabel>
              <Select
                value={bathrooms || ''}
                onChange={(e) => setBathrooms(e.target.value)}
              >
                {dropdownData.bathrooms.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Parking Spaces */}
          <Grid item xs={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Parking Spaces</InputLabel>
              <Select
                value={parkingSpaces || ''}
                onChange={(e) => setParkingSpaces(e.target.value)}
              >
                {dropdownData.parkingSpaces.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Basement */}
          <Grid item xs={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Basement</InputLabel>
              <Select
                value={basement || ''}
                onChange={(e) => setBasement(e.target.value)}
              >
                {dropdownData.basementOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Approx Age */}
          <Grid item xs={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Approx Age</InputLabel>
              <Select
                value={approxAge || ''}
                onChange={(e) => setApproxAge(e.target.value)}
              >
                {dropdownData.ageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* A/C */}
          <Grid item xs={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>A/C</InputLabel>
              <Select
                value={ac || ''}
                onChange={(e) => setAc(e.target.value)}
              >
                {dropdownData.acOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Square Footage */}
          <Grid item xs={12}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Square Footage</InputLabel>
              <Select
                value={squareFootage || ''}
                onChange={(e) => setSquareFootage(e.target.value)}
              >
                {dropdownData.squareFootRanges.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Button variant="contained" color="primary" onClick={handleSave}>
              Save Property
            </Button>
          </Grid>
        </Grid>
      </div>
    </Modal>
  );
};

export default CustomPropertyModal;